import './main.scss';
import React, {useCallback, useEffect, useState} from 'react';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';
import Header from '../../../organisms/header/header.js';
import decodeToken from '../../../../utils/decode-token';
import TableHac from '../../../organisms/role-pma/table-hac/table-hac';
import TablePma from '../../../organisms/role-pma/table-pma/table-pma';
import TableRetailer from '../../../organisms/role-pma/table-retailer/table-retailer';
import {useTranslation} from "react-i18next";
import ModalNominateOnBehalf from "../../../organisms/role-admin/modal-nominate-on-behalf/modal-nominate-on-behalf";
import Calendar from "../../../organisms/common/calendar/calendar";
import RoleRibbon from '../../../organisms/common/role-ribbon/role-ribbon';
import { getPmaMainTabs } from '../../../organisms/common/role-ribbon/role-ribbon.config';
import { isPmaManagerRole, isPmaRole } from '../../../../utils/role-utils';
import MainSectionHeader from '../../../organisms/common/main-section-header/main-section-header';
import MainTabPanel from '../../../organisms/common/main-tab-panel/main-tab-panel';

const PMA_TAB_ROUTES = {
  hac: '/pma/main/hac',
  pma: '/pma/main/pma',
  retailer: '/pma/main/retailers',
  calendar: '/pma/calendar',
};

const INITIAL_RELOAD_STATE = {
  hac: true,
  pma: true,
  retailer: true,
};

function PmaMain() {

  const {t} = useTranslation()

  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;

  const location = useLocation();
  const navigate = useNavigate();
  const canManagePmas = isPmaManagerRole(role);

  const [activeTab, setActiveTab] = useState('calendar');
  const [reloadTabs, setReloadTabs] = useState(INITIAL_RELOAD_STATE);

  const [openModal, setOpenModal] = useState(false);

  const reloadTab = useCallback((tab) => {
    if (tab === 'calendar') {
      return;
    }

    setReloadTabs((current) => ({ ...current, [tab]: true }));
  }, []);

  useEffect(() => {
    if (location.pathname.includes('main/hac')) {
      setActiveTab('hac');
      reloadTab('hac');
    } else if (location.pathname.includes('main/pma')) {
      if (!canManagePmas) {
        navigate('/pma/main/hac', { replace: true });
        return;
      }
      setActiveTab('pma');
      reloadTab('pma');
    } else if (location.pathname.includes('main/retailer')) {
      setActiveTab('retailer');
      reloadTab('retailer');
    } else if (location.pathname.includes('calendar')) {
      setActiveTab('calendar');
    }
  }, [location.pathname, canManagePmas, navigate, reloadTab]);

  const handleTabClick = (tab) => {
    if (tab === 'pma' && !canManagePmas) {
      return;
    }

    setActiveTab(tab);
    reloadTab(tab);
    navigate(PMA_TAB_ROUTES[tab]);
  };

  const handleDataLoaded = useCallback(() => {
    if (activeTab === 'calendar') {
      return;
    }

    setReloadTabs((current) => ({ ...current, [activeTab]: false }));
  }, [activeTab]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const loggedUser = JSON.parse(sessionStorage.getItem('loggedUser') || null);
  const canNominate = loggedUser?.is_nominator;
  const getLabel = (key, defaultValue) => t(key, { defaultValue });
  const topTabs = getPmaMainTabs(activeTab);
  const userTabs = [
    { id: 'hac', label: t('common-rrc-members') },
    ...(canManagePmas ? [{ id: 'pma', label: t('admin-main-tabs-tab2') }] : []),
  ];
  const nominateButton = canNominate === true || canNominate === 1 ? (
    <button className="btn secondary" onClick={handleOpenModal}>
      <span className="icon-plus"></span> {t('admin-main-tab1-action')}
    </button>
  ) : null;
  const newPmaButton = (
    <a href="/pma/new-pma" className="btn secondary">+ {t('admin-main-tab2-action')}</a>
  );

  const renderUserSectionHeader = (action = null) => (
    <MainSectionHeader
      title={getLabel('common-users', 'Users')}
      tabs={userTabs}
      activeTab={activeTab}
      onTabClick={handleTabClick}
      action={action}
    />
  );

  return (
    <>
      {token && isPmaRole(role) ? (
          <div className="App">
            <Header/>
            <main>
              <RoleRibbon tabs={topTabs} navigate={navigate} translate={t} />
              <MainTabPanel active={activeTab === 'calendar'}>
                <header>
                  <h2>{t('common-calendar')}</h2>
                </header>
                <Calendar />
              </MainTabPanel>

              <MainTabPanel active={activeTab === 'hac'}>
                {renderUserSectionHeader(nominateButton)}
                <TableHac
                  reload={reloadTabs.hac}
                  onDataLoaded={handleDataLoaded}
                />
              </MainTabPanel>

              <MainTabPanel active={activeTab === 'retailer'}>
                {renderUserSectionHeader()}
                <TableRetailer
                  reload={reloadTabs.retailer}
                  onDataLoaded={handleDataLoaded}
                />
              </MainTabPanel>

              <MainTabPanel active={activeTab === 'pma'}>
                {renderUserSectionHeader(newPmaButton)}
                <TablePma
                  reload={reloadTabs.pma}
                  onDataLoaded={handleDataLoaded}
                />
              </MainTabPanel>
            </main>
            <ModalNominateOnBehalf open={openModal} onClose={handleCloseModal}/>
          </div>
        ) :
        (
          <Navigate to="/login"/>
        )
      }
    </>
  )
    ;
}

export default PmaMain;
